import { useMemo } from "react";
import { Box, Text } from "@radix-ui/themes";
import { useI18n } from "@/i18n";
import { formatDateDisplay } from "@/lib/dates";
import { formatDelta, formatValue } from "@/lib/format";
import type { ObservationPoint, Unit } from "@/types";

interface SeriesTableProps {
  data: ObservationPoint[];
  unit: Unit;
}

interface Row {
  date: string;
  value: number;
  delta: number | null;
}

export function SeriesTable({ data, unit }: SeriesTableProps) {
  const { t, locale } = useI18n();

  const rows = useMemo<Row[]>(() => {
    // Render most recent first so the freshest figures are always visible without scrolling.
    return [...data]
      .sort((a, b) => (a.date < b.date ? 1 : -1))
      .map((point, i, sorted) => ({
        date: point.date,
        value: point.value,
        delta: i < sorted.length - 1 ? point.value - sorted[i + 1].value : null,
      }));
  }, [data]);

  return (
    <Box className="rk-table-wrap">
      <table className="rk-table">
        <thead>
          <tr>
            <th>{t("table.date")}</th>
            <th className="rk-table-num">{t("table.value")}</th>
            <th className="rk-table-num">{t("table.change")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.date}>
              <td>{formatDateDisplay(row.date, locale)}</td>
              <td className="rk-table-num rk-mono">{formatValue(row.value, unit)}</td>
              <td className="rk-table-num rk-mono">
                {row.delta === null ? (
                  <Text size="1" color="gray">
                    {t("table.noChange")}
                  </Text>
                ) : (
                  <span className={row.delta > 0 ? "rk-delta-up" : row.delta < 0 ? "rk-delta-down" : "rk-delta-flat"}>
                    {formatDelta(row.delta, unit)}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
}
